import React from "react";
import { renderEditableCell } from "../../utils";
import { TooltipWithContent } from "@/components/ui/tooltip";
import { RowActions } from "./RowActions";
import { useOpenModal } from "@/lib/modals";
import { getModalConfig, isModalConfigured } from "@/lib/modals/url/modal-url-config";
import { useGlobalModalStack } from "@wtc/ui";
import type {
  BaseEntity,
  SimpleTableConfig,
  SimpleFieldConfig,
  UniversalInlineEditHandler,
  FieldValue,
  EntityHooks,
} from "../../types";
import type { SimpleTableContextValue } from "../../contexts/SimpleTableContext";
import type { AuthenticatedUser } from "@/lib/types/unified";

export interface TitleCellProps<TData extends BaseEntity> {
  fieldId: string;
  fieldValue: FieldValue;
  fieldConfig: SimpleFieldConfig;
  item: TData;
  isEditing: boolean;
  config: SimpleTableConfig<TData>;
  state: SimpleTableContextValue<TData>["state"];
  callbacks: SimpleTableContextValue<TData>["callbacks"];
  auth: { appUser: AuthenticatedUser | null; roleName: string | null };
  data: TData[];
  inlineEditHandler: UniversalInlineEditHandler;
  onOpenDetailModal?: (item: TData, rowIds: string[], index: number) => void;
  dataRowIds?: string[];
  dataIndex?: number;
  hooks?: EntityHooks<TData>;
  onEdit?: (item: TData) => void;
  onView?: (item: TData) => void;
  onDelete?: (item: TData) => void;
}

/**
 * TitleCell renders title/name fields with view action support
 * Supports inline editing and optional row actions
 * 
 * Row actions are shown when:
 * 1. fieldConfig.showRowActions is true
 * 2. config.rowActionsPlacement is "inlineTitle"
 * 3. detailActions are configured
 */
export function TitleCell<TData extends BaseEntity>({
  fieldId,
  fieldValue,
  fieldConfig,
  item,
  isEditing,
  config,
  state,
  callbacks,
  auth,
  data,
  inlineEditHandler,
  onOpenDetailModal,
  dataRowIds,
  dataIndex,
  hooks,
  onEdit,
  onView,
  onDelete,
}: TitleCellProps<TData>) {
  const openModal = useOpenModal();
  const { openModal: openGlobalStackModal } = useGlobalModalStack();
  const shouldShowActions = fieldConfig.showRowActions === true;

  const handleOpenDetail = () => {
    // Entities whose detail view is a bespoke global-modal-stack modal (e.g.
    // admin users → UserDetailModal) declare `detailModalId` + `detailModalParam`
    // and bypass the URL-config `${entity}-detail` path entirely.
    if (config.detailModalId) {
      openGlobalStackModal(config.detailModalId, {
        [config.detailModalParam ?? "id"]: item.id,
      });
      return;
    }

    // Use onOpenDetailModal callback if available, otherwise fall back to pushState
    if (onOpenDetailModal && dataRowIds && dataIndex !== undefined) {
      onOpenDetailModal(item, dataRowIds, dataIndex);
      return;
    }

    // Fallback: Open detail modal through the centralized hub modal controller.
    const modalId = `${config.entity}-detail`;
    if (!isModalConfigured(modalId)) {
      return;
    }
    const modalConfig = getModalConfig(modalId);
    openModal(modalId, { [modalConfig.pluralParam]: item.id });
  };

  // When editing, render without tooltip
  if (isEditing) {
    return (
      <div className="text-left hover:text-blue-600 hover:underline cursor-pointer transition-colors duration-200 w-full break-words">
        {renderEditableCell<TData>(
          fieldValue,
          fieldConfig,
          item.id,
          fieldId,
          state.editingCell,
          state.editValue,
          callbacks.handleCellEditCallback,
          callbacks.handleCellSaveCallback,
          callbacks.handleCellCancelCallback,
          state.setEditValue,
          data,
          inlineEditHandler,
          auth.appUser || undefined,
          true, // isTitleField - to avoid nested buttons
          undefined, // customDisplayText
          auth.roleName || undefined
        )}
      </div>
    );
  }

  // When not editing, render with tooltip and optional inline actions
  return (
    <div className={`flex items-center ${shouldShowActions ? "justify-between" : "justify-start"} w-full gap-2`}>
      <TooltipWithContent content="Click to view details">
        <div
          onClick={handleOpenDetail}
          className="text-left hover:text-blue-600 hover:underline cursor-pointer transition-colors duration-200 flex-1 break-words"
        >
          {renderEditableCell<TData>(
            fieldValue,
            fieldConfig,
            item.id,
            fieldId,
            state.editingCell,
            state.editValue,
            callbacks.handleCellEditCallback,
            callbacks.handleCellSaveCallback,
            callbacks.handleCellCancelCallback,
            state.setEditValue,
            data,
            inlineEditHandler,
            auth.appUser || undefined,
            true // isTitleField - to avoid nested buttons
          )}
        </div>
      </TooltipWithContent>

      {/* Inline Actions when field has showRowActions enabled */}
      {shouldShowActions &&
        config.rowActionsPlacement === "inlineTitle" &&
        hooks &&
        config.detailActions &&
        config.detailActions.length > 0 && (
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <RowActions<TData>
              item={item}
              config={config}
              hooks={hooks}
              onEdit={onEdit}
              onView={onView}
              onDelete={onDelete}
              compact={config.rowActionsDisplayMode !== "labelOnly"}
            />
          </div>
        )}
    </div>
  );
}
